import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { renderCurrencyIconColored } from "../../../utils/CurrencyUtils";
import { formatIntegerAmount } from "../../../utils/CommonFunction";
import {
  calculateProposalPayments,
  getProposalViewPayments,
} from "../../../services/contractorService";
import { store } from "../../../redux/store/configureStore";
import { getContractorId } from "../../../utils/tokenUtils";

const RENO_FEE_TYPES = [
  { value: "CUSTOMER_PAID", label: "Customer Paid" },
  { value: "CONTRACTOR_PAID", label: "Contractor Paid" },
  { value: "SPLIT", label: "Split" },
];

// Maps the UI's reno-fee type → backend `strategy` enum used by the
// calculate-payments / save-payments contracts.
const STRATEGY_BY_TYPE = {
  CUSTOMER_PAID: "CUSTOMER_ONLY",
  CONTRACTOR_PAID: "CONTRACTOR_ONLY",
  SPLIT: "SPLIT",
};

// Reverse of STRATEGY_BY_TYPE — used when /reno-fees returns an
// already-configured proposal and we need to seed the UI dropdown back.
const TYPE_BY_STRATEGY = {
  CUSTOMER_ONLY: "CUSTOMER_PAID",
  CONTRACTOR_ONLY: "CONTRACTOR_PAID",
  SPLIT: "SPLIT",
};

const todayIso = () => new Date().toISOString().split("T")[0];

const formatWithCommas = (val) => {
  const digits = String(val ?? "").replace(/[^\d]/g, "");
  return digits ? Number(digits).toLocaleString("en-US") : "";
};

const stripDigits = (val) => String(val ?? "").replace(/[^\d]/g, "");

const formatDateDisplay = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const floatingFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    fontFamily: "Inter Tight",
    boxShadow: "none",
    "&.Mui-focused": { boxShadow: "none" },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "10px",
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-input": {
    fontFamily: "Inter Tight",
    fontSize: 14,
    color: "#0D0D0E",
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "&[type=number]": { MozAppearance: "textfield" },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Inter Tight",
    color: "#6B7280",
  },
  "& .MuiOutlinedInput-input.Mui-disabled": {
    WebkitTextFillColor: "#0D0D0E",
    color: "#0D0D0E",
    cursor: "not-allowed",
  },
};

const lockedShellSx = {
  "& .MuiOutlinedInput-root.Mui-disabled fieldset": {
    borderColor: "#E5E7EB",
  },
  "& .MuiInputLabel-root.Mui-disabled": { color: "#6B7280" },
  "& .MuiOutlinedInput-root.Mui-disabled": {
    backgroundColor: "#fff",
    cursor: "not-allowed",
  },
};

const SectionCard = ({ title, icon, headerRight, children }) => (
  <Box
    sx={{
      width: { xs: "100%", md: "80%" },
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      backgroundColor: "#fff",
    }}
  >
    <Box
      sx={{
        px: 2.5,
        py: 1.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        paddingBottom: !headerRight && 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        {icon}
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {headerRight}
      </Box>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

const PrimaryButton = styled(Button)(() => ({
  backgroundColor: "#4D6BF6",
  color: "#fff",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "10px",
  boxShadow: "none",
  padding: "8px 18px",
  fontSize: 13,
  "&:hover": { backgroundColor: "#3B5BDB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#A8B5F8", color: "#fff" },
}));

const ShareNameInput = ({ value, onChange, disabled }) => (
  <TextField
    size="small"
    value={value ?? ""}
    onChange={onChange}
    disabled={disabled}
    InputLabelProps={{ shrink: true }}
    sx={{
      ...floatingFieldSx,
      ...lockedShellSx,
      width: { xs: "100%", md: 220 },
      justifySelf: "start",
    }}
  />
);

const AmountCell = ({ value, onChange, disabled, readOnly = false }) => (
  <TextField
    fullWidth
    size="small"
    label={
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        Amount ({renderCurrencyIconColored("black-xs-small")})
      </Box>
    }
    placeholder="0"
    value={formatWithCommas(value)}
    onChange={readOnly ? undefined : onChange}
    disabled={disabled}
    InputProps={{ readOnly }}
    InputLabelProps={{ shrink: true }}
    sx={{
      ...floatingFieldSx,
      ...lockedShellSx,
      // Read-only single-share rows (CUSTOMER_PAID / CONTRACTOR_PAID)
      // keep the same chrome as a live input but become non-interactive:
      // white background, black text, not-allowed cursor.
      ...(readOnly
        ? {
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#fff !important",
              cursor: "not-allowed",
            },
            "& .MuiInputBase-input": {
              color: "#0D0D0E !important",
              WebkitTextFillColor: "#0D0D0E !important",
              cursor: "not-allowed",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E7EB",
            },
          }
        : {}),
    }}
  />
);

// Two-column "label | value" rows for the View-Reno-Fee summary at the
// top of the Reno Fees card. Keeps the look aligned with the other
// section cards (no inputs — pure read-only display).
const RenoFeeMetaTable = ({ meta, loading }) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (!meta) return null;

  const planLabel = meta.applicablePlanName || meta.applicablePlanCode || "-";
  const rows = [
    {
      label: "Tier",
      value: (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.25,
            py: 0.25,
            borderRadius: "999px",
            backgroundColor: "#F3E8FF",
            color: "#7C3AED",
            fontFamily: "Inter Tight",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {planLabel}
        </Box>
      ),
    },
    {
      label: "Setup Fee",
      value: (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          {renderCurrencyIconColored("black-xs-small")}
          {meta.setupFee != null ? formatIntegerAmount(meta.setupFee) : "-"}
        </Box>
      ),
    },
    {
      label: "Reno Fee",
      value: (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          {renderCurrencyIconColored("black-xs-small")}
          {meta.renoFee != null ? formatIntegerAmount(meta.renoFee) : "-"}
        </Box>
      ),
    },
    {
      label: "Proposal Cost",
      value: (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          {renderCurrencyIconColored("black-xs-small")}
          {meta.proposalCost != null
            ? formatIntegerAmount(meta.proposalCost)
            : "-"}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ mb: 2.5 }}>
      {rows.map((r) => (
        <Box
          key={r.label}
          sx={{
            display: "grid",
            gridTemplateColumns: "200px 1fr",
            alignItems: "center",
            px: 2,
            py: 1.25,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              color: "#6B7280",
              fontFamily: "Inter Tight",
              fontWeight: 500,
            }}
          >
            {r.label}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              fontSize: 14,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
              fontWeight: 600,
            }}
          >
            {r.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const Payments = forwardRef(function Payments(
  {
    proposalId,
    projectId,
    onEditMilestones,
    onPaymentsSavedChange,
    isReadOnly = false,
    // Full billing_milestones array from the contractor proposal endpoint.
    // Each entry has { id, groupName, ... } and feeds the dropdown next to
    // each Customer Payment row so users can map a payment to a milestone.
    billingMilestones,
  },
  ref
) {
  // Local user edits to the customer payment schedule (currently just
  // the billingMilestoneId selection per row). Keyed by row index into
  // calcResponse.paymentPlan.paymentSchedule. Reset whenever a fresh
  // calculate / view response replaces the schedule.
  const [scheduleOverrides, setScheduleOverrides] = useState({});
  // IDs of billing milestones that haven't been attached to any
  // payment line. Populated when save validation runs; rendered as a
  // red callout above the Customer Payments section to point the user
  // at exactly which milestones still need a payment line.
  const [missingMilestoneIds, setMissingMilestoneIds] = useState([]);
  // Reno-fee meta from GET /reno-fees — drives the 2-col table at the top
  // of the Reno Fees card.
  const [renoFeeMeta, setRenoFeeMeta] = useState(null);
  const [renoFeeMetaLoading, setRenoFeeMetaLoading] = useState(false);

  const [renoFeeType, setRenoFeeType] = useState("");
  const [renoFeeAdded, setRenoFeeAdded] = useState(false);
  const [calculating, setCalculating] = useState(false);

  // Server response from POST /calculate-payments — drives the Customer
  // Payments + Settlements section content. Held here so the parent's
  // bottom-Save can re-use the same body for /save-payments.
  const [calcResponse, setCalcResponse] = useState(null);
  const [lastRequestBody, setLastRequestBody] = useState(null);

  const [contractorShare, setContractorShare] = useState({
    name: "Contractor Share",
    amount: "",
    dueDate: todayIso(),
  });
  const [customerShare, setCustomerShare] = useState({
    name: "Customer Share",
    amount: "",
    dueDate: todayIso(),
  });

  // Fetch View Payments on mount. The endpoint always returns the
  // `renoFee` tier-defaults object; the *setup* branch additionally
  // includes a `billingId` (plus strategy / feeItems / paymentPlan /
  // contractorPayouts). We treat the presence of billingId as the sole
  // setup signal — comparing against `data.renoFee` no longer works
  // because the backend now returns it in both branches.
  // GET /api/v2/contractor/proposal/{proposalId}/reno-fees.
  useEffect(() => {
    if (!proposalId) return undefined;
    let cancelled = false;
    (async () => {
      setRenoFeeMetaLoading(true);
      try {
        const res = await getProposalViewPayments(proposalId);
        if (cancelled) return;
        const data = res?.data || res || null;
        if (!data) return;

        const isSetup =
          data.billingId !== undefined &&
          data.billingId !== null &&
          data.billingId !== "";

        if (!isSetup) {
          // Reno fee not setup — just show tier defaults.
          if (data.renoFee && typeof data.renoFee === "object") {
            setRenoFeeMeta(data.renoFee);
          }
          if (typeof onPaymentsSavedChange === "function") {
            onPaymentsSavedChange(false);
          }
          return;
        }

        // Reno fee already setup — seed UI from server state. The setup
        // branch puts the tier defaults (applicablePlanName, setupFee,
        // proposalCost, transactionFeePercent) on the inner `renoFee`
        // object; we merge those on top of the configured `renoFees`
        // total so the meta table renders the full tier row.
        const tierDefaults =
          data.renoFee && typeof data.renoFee === "object" ? data.renoFee : {};
        setRenoFeeMeta({
          ...tierDefaults,
          proposalId: data.proposalId,
          renoFee: data.renoFees,
        });

        const seededType = TYPE_BY_STRATEGY[data.strategy] || "";
        setRenoFeeType(seededType);

        const feeItems = Array.isArray(data.feeItems) ? data.feeItems : [];
        const contrItem = feeItems.find((f) => f.type === "CONTRACTOR_PAID");
        const custItem = feeItems.find((f) => f.type === "CUSTOMER_PAID");
        const today = todayIso();
        setContractorShare({
          name: contrItem?.displayName || "Contractor Share",
          amount: contrItem ? String(contrItem.amount ?? "") : "",
          dueDate: today,
        });
        setCustomerShare({
          name: custItem?.displayName || "Customer Share",
          amount: custItem ? String(custItem.amount ?? "") : "",
          dueDate: today,
        });

        setCalcResponse({
          paymentPlan: data.paymentPlan || null,
          contractorPayouts: Array.isArray(data.contractorPayouts)
            ? data.contractorPayouts
            : [],
        });
        setRenoFeeAdded(true);
        if (typeof onPaymentsSavedChange === "function") {
          onPaymentsSavedChange(true);
        }
      } catch (e) {
        console.error("Failed to fetch view-payments:", e);
      } finally {
        if (!cancelled) setRenoFeeMetaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  // Compute the prefill values for the Customer / Contractor share fields
  // based on the selected reno-fee strategy. The split base is the
  // `renoFee` returned by GET /reno-fees — for SPLIT we halve it, for
  // CUSTOMER_PAID / CONTRACTOR_PAID one side gets the full reno fee.
  const computePrefills = (type, meta) => {
    const baseRenoFee = Number(meta?.renoFee) || 0;
    if (!type || baseRenoFee <= 0) {
      return { customer: "", contractor: "" };
    }
    if (type === "CUSTOMER_PAID") {
      return { customer: String(baseRenoFee), contractor: "" };
    }
    if (type === "CONTRACTOR_PAID") {
      return { customer: "", contractor: String(baseRenoFee) };
    }
    if (type === "SPLIT") {
      const half = Math.round(baseRenoFee / 2);
      const otherHalf = baseRenoFee - half;
      return { customer: String(half), contractor: String(otherHalf) };
    }
    return { customer: "", contractor: "" };
  };

  // Resetting share rows when the user (re)selects a Reno Fee Type —
  // values are prefilled from the renoFee tier and remain editable.
  const handleTypeChange = (newType) => {
    setRenoFeeType(newType);
    const today = todayIso();
    const { customer, contractor } = computePrefills(newType, renoFeeMeta);
    setCustomerShare({
      name: "Customer Share",
      amount: customer,
      dueDate: today,
    });
    setContractorShare({
      name: "Contractor Share",
      amount: contractor,
      dueDate: today,
    });
  };

  // If the meta arrives after the user has already selected a type (and
  // the row hasn't been Add'd yet), top up the prefills.
  useEffect(() => {
    if (!renoFeeMeta || !renoFeeType || renoFeeAdded) return;
    const { customer, contractor } = computePrefills(renoFeeType, renoFeeMeta);
    setCustomerShare((s) => ({
      ...s,
      amount: s.amount && Number(s.amount) > 0 ? s.amount : customer,
    }));
    setContractorShare((s) => ({
      ...s,
      amount: s.amount && Number(s.amount) > 0 ? s.amount : contractor,
    }));
  }, [renoFeeMeta, renoFeeType, renoFeeAdded]);

  // When the parent says the proposal is no longer editable (status !==
  // DRAFT), force the locked view regardless of whether reno-fees were
  // ever set up — this keeps the read-only inputs / hidden CTAs path.
  const isLocked = renoFeeAdded || isReadOnly;

  // Build the body used by both /calculate-payments and /save-payments
  // (they share the contract). renoFees = the *contractor-side* fee
  // amount the customer / contractor is reserving — for SPLIT we send
  // the total and emit two feeItems.
  const buildPaymentsRequestBody = () => {
    const token = store.getState()?.auth?.token;
    const contractorId = getContractorId(token);

    const strategy = STRATEGY_BY_TYPE[renoFeeType] || null;
    const contrAmt = Number(contractorShare.amount) || 0;
    const custAmt = Number(customerShare.amount) || 0;

    const contractorName = contractorShare.name || "Contractor Share";
    const customerName = customerShare.name || "Customer Share";

    let renoFees = 0;
    let feeItems = [];
    if (renoFeeType === "CONTRACTOR_PAID") {
      renoFees = contrAmt;
      feeItems = [
        {
          type: "CONTRACTOR_PAID",
          displayName: contractorName,
          amount: contrAmt,
        },
      ];
    } else if (renoFeeType === "CUSTOMER_PAID") {
      renoFees = custAmt;
      feeItems = [
        {
          type: "CUSTOMER_PAID",
          displayName: customerName,
          amount: custAmt,
        },
      ];
    } else if (renoFeeType === "SPLIT") {
      renoFees = contrAmt + custAmt;
      feeItems = [
        {
          type: "CONTRACTOR_PAID",
          displayName: contractorName,
          amount: contrAmt,
        },
        {
          type: "CUSTOMER_PAID",
          displayName: customerName,
          amount: custAmt,
        },
      ];
    }

    return {
      contractorId,
      projectId: projectId || null,
      renoFees,
      strategy,
      feeItems,
    };
  };

  const validateBeforeCalculate = () => {
    if (!proposalId) {
      toast.error("Save the proposal first");
      return false;
    }
    if (!renoFeeType) {
      toast.error("Please select a Reno Fee Type");
      return false;
    }
    // Per-strategy amount checks — the entered amount(s) must reconcile
    // back to the platform's Reno Fee total. Read-only single-share
    // modes (CUSTOMER_PAID / CONTRACTOR_PAID) are pre-filled to the
    // Reno Fee already; SPLIT lets the user choose any split as long
    // as it adds up.
    const baseRenoFee = Number(renoFeeMeta?.renoFee) || 0;
    if (renoFeeType === "CUSTOMER_PAID") {
      const cust = Number(customerShare.amount) || 0;
      if (cust <= 0) {
        toast.error("Customer Share amount must be greater than zero");
        return false;
      }
      if (baseRenoFee > 0 && cust !== baseRenoFee) {
        toast.error(`Customer Share amount should equal the Reno Fee (${baseRenoFee})`);
        return false;
      }
    } else if (renoFeeType === "CONTRACTOR_PAID") {
      const contr = Number(contractorShare.amount) || 0;
      if (contr <= 0) {
        toast.error("Contractor Share amount must be greater than zero");
        return false;
      }
      if (baseRenoFee > 0 && contr !== baseRenoFee) {
        toast.error(`Contractor Share amount should equal the Reno Fee (${baseRenoFee})`);
        return false;
      }
    } else if (renoFeeType === "SPLIT") {
      const contr = Number(contractorShare.amount) || 0;
      const cust = Number(customerShare.amount) || 0;
      if (contr <= 0 || cust <= 0) {
        toast.error("Both Contractor and Customer shares must be greater than zero");
        return false;
      }
      if (baseRenoFee > 0 && contr + cust !== baseRenoFee) {
        toast.error(
          `Sum of Customer + Contractor share should equal the Reno Fee (${baseRenoFee})`,
        );
        return false;
      }
    }
    return true;
  };

  const handleAddRenoFee = async () => {
    if (!validateBeforeCalculate()) return;
    const body = buildPaymentsRequestBody();
    setCalculating(true);
    try {
      const res = await calculateProposalPayments(proposalId, body);
      const data = res?.data || res || null;
      setCalcResponse(data);
      setLastRequestBody(body);
      setRenoFeeAdded(true);
    } catch (e) {
      console.error("Calculate payments error:", e);
      toast.error(e?.message || "Failed to calculate payments");
    } finally {
      setCalculating(false);
    }
  };

  const handleUpdateRenoFee = () => {
    setRenoFeeAdded(false);
  };

  useImperativeHandle(ref, () => ({
    isReady: () => isLocked && !!renoFeeType && !!calcResponse,
    validate: () => {
      if (!renoFeeType) {
        toast.error("Please select a Reno Fee Type");
        return false;
      }
      if (!isLocked || !calcResponse) {
        toast.error("Add the Reno Fee before saving");
        return false;
      }
      // Every billing milestone must end up attached to exactly one
      // customer-payment line. Collect what the user selected across
      // all non-Reno-Fee rows and diff against the milestone catalog;
      // any unattached milestone blocks the save with a named error.
      const attached = new Set();
      customerSchedule.forEach((row, idx) => {
        if (isRenoFeeRow(row)) return;
        const v = resolveMilestoneId(idx);
        if (v !== "" && v != null) attached.add(String(v));
      });
      const missing = (billingMilestones || []).filter(
        (m) => m && m.id != null && !attached.has(String(m.id)),
      );
      if (missing.length > 0) {
        const names = missing
          .map((m) => m.groupName || m.description || `Milestone ${m.id}`)
          .join(", ");
        setMissingMilestoneIds(missing.map((m) => m.id));
        toast.error(
          `Attach a payment line to each billing milestone before saving: ${names}`,
        );
        return false;
      }
      setMissingMilestoneIds([]);
      return true;
    },
    // Body for POST /save-payments — same shape as /calculate-payments,
    // plus the customer-facing paymentSchedule with the milestone IDs
    // the user picked in the dropdown.
    buildSavePayload: () => {
      const base = lastRequestBody || buildPaymentsRequestBody();
      return {
        ...base,
        paymentSchedule: buildPaymentScheduleForSave(),
      };
    },
  }));

  const customerSchedule = Array.isArray(
    calcResponse?.paymentPlan?.paymentSchedule
  )
    ? calcResponse.paymentPlan.paymentSchedule
    : [];
  // Contractor settlements mirror the contractor payout array, but the
  // Initial Deposit row always anchors at the top regardless of where
  // the backend slots it in the array.
  const settlementsSchedule = useMemo(() => {
    const list = Array.isArray(calcResponse?.contractorPayouts)
      ? calcResponse.contractorPayouts
      : [];
    const isInitial = (s) =>
      s?.referenceType === "PROJECT_INITIAL_DEPOSIT" ||
      s?.paymentGroup?.type === "PROJECT_INITIAL_DEPOSIT";
    return [
      ...list.filter(isInitial),
      ...list.filter((s) => !isInitial(s)),
    ];
  }, [calcResponse]);

  // Reset the per-row overrides whenever the schedule from the server
  // changes (initial load / recalculate). Server-supplied
  // billingMilestoneId is the source of truth on first paint; the
  // overrides map only kicks in when the user picks a different
  // milestone via the dropdown.
  useEffect(() => {
    setScheduleOverrides({});
    setMissingMilestoneIds([]);
  }, [calcResponse]);

  // Base options derived from billing_milestones, plus a sidecar map
  // that captures the original milestone object so we can branch on
  // type (PROJECT_INITIAL_DEPOSIT) further down.
  const milestoneOptionsRaw = useMemo(() => {
    const list = Array.isArray(billingMilestones) ? billingMilestones : [];
    return list
      .filter((m) => m && m.id != null)
      .map((m) => ({
        value: m.id,
        label: m.groupName || m.description || `Milestone ${m.id}`,
        type: String(m.type || "").toUpperCase(),
      }));
  }, [billingMilestones]);

  // The single milestone of type PROJECT_INITIAL_DEPOSIT, if present —
  // reserved for the "Initial Deposit" payment row.
  const initialDepositMilestoneId = useMemo(() => {
    const m = milestoneOptionsRaw.find(
      (o) => o.type === "PROJECT_INITIAL_DEPOSIT",
    );
    return m?.value ?? null;
  }, [milestoneOptionsRaw]);

  // Resolve the current billingMilestoneId for a row — user override
  // wins; otherwise fall back to what the server gave us.
  const resolveMilestoneId = (idx) => {
    if (scheduleOverrides[idx] !== undefined) return scheduleOverrides[idx];
    const fromServer = customerSchedule[idx]?.billingMilestoneId;
    return fromServer ?? "";
  };

  const handleMilestoneSelect = (idx, value) => {
    setScheduleOverrides((prev) => ({
      ...prev,
      [idx]: value === "" ? null : Number(value),
    }));
    // User is fixing things — drop any stale "missing" red banner so
    // it doesn't keep nagging while they re-attach milestones.
    setMissingMilestoneIds([]);
  };

  // Heuristic — is this customer-payment row the Initial Deposit slot?
  // The schedule entry coming from the backend uses type=DOWNPAYMENT
  // for the deposit; we also accept a matching display name as a
  // belt-and-braces fallback.
  const isDepositRow = (row) => {
    if (!row) return false;
    if (String(row.type || "").toUpperCase() === "DOWNPAYMENT") return true;
    return (
      String(row.displayName || "").trim().toLowerCase() === "initial deposit"
    );
  };

  // Reno-fee rows are server-synthesised; they represent the platform
  // fee withdrawal and aren't tied to any contractor billing milestone.
  const isRenoFeeRow = (row) =>
    String(row?.type || "").toUpperCase() === "RENO_FEE";

  // Build the per-row option list:
  //   • Initial Deposit row → only the deposit milestone (other types
  //     are filtered out so the user can't pick something else).
  //   • Every other row → all non-deposit milestones (deposit option
  //     hidden so it can't be reused).
  //   • Any milestone already chosen on a *different* row is marked
  //     `disabled: true` so one milestone can attach to one payment
  //     line. The row's own selection stays selectable.
  const optionsForRow = (idx) => {
    const row = customerSchedule[idx];
    const deposit = isDepositRow(row);
    const currentSelection = resolveMilestoneId(idx);

    const usedElsewhere = new Set();
    customerSchedule.forEach((_, otherIdx) => {
      if (otherIdx === idx) return;
      const v = resolveMilestoneId(otherIdx);
      if (v !== "" && v != null) usedElsewhere.add(String(v));
    });

    return milestoneOptionsRaw
      .filter((o) => {
        if (deposit) return o.value === initialDepositMilestoneId;
        return o.value !== initialDepositMilestoneId;
      })
      .map((o) => ({
        value: o.value,
        label: o.label,
        disabled:
          usedElsewhere.has(String(o.value)) &&
          String(o.value) !== String(currentSelection),
      }));
  };

  // Final paymentSchedule array sent on /save-payments: keep every
  // server-side field on each row, overlay the user's selected
  // billingMilestoneId, and drop synthetic Reno Fee rows entirely —
  // the backend re-derives them from `renoFees` + `feeItems` on its
  // side, so echoing them back would duplicate the entry.
  const buildPaymentScheduleForSave = () =>
    customerSchedule
      .map((row, idx) => ({ row, idx }))
      .filter(({ row }) => !isRenoFeeRow(row))
      .map(({ row, idx }) => {
        const next = { ...row };
        const overrideMilestoneId = resolveMilestoneId(idx);
        if (overrideMilestoneId === "" || overrideMilestoneId == null) {
          delete next.billingMilestoneId;
        } else {
          next.billingMilestoneId = overrideMilestoneId;
        }
        return next;
      });

  // Grid columns for the Reno Fees body — fixed 2-col layout
  // (label | amount) for share rows; the dropdown row uses the same
  // grid with the Add/Update button in the right column.
  const renoFeeGridCols = { xs: "1fr", md: "1.4fr 0.9fr" };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <SectionCard
        title="Reno Fees"
        icon={<PaletteOutlinedIcon sx={{ color: "#4D6BF6", fontSize: 20 }} />}
      >
        <RenoFeeMetaTable meta={renoFeeMeta} loading={renoFeeMetaLoading} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: renoFeeGridCols,
            columnGap: 2.5,
            rowGap: 3,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            select
            label="Reno Fee Type"
            value={renoFeeType}
            onChange={(e) => handleTypeChange(e.target.value)}
            disabled={isLocked || calculating}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) {
                  return (
                    <Box
                      component="span"
                      sx={{
                        color: "#9CA3AF",
                        fontFamily: "Inter Tight",
                        fontSize: 14,
                      }}
                    >
                      Select Reno Fee Type
                    </Box>
                  );
                }
                const match = RENO_FEE_TYPES.find((t) => t.value === selected);
                return match ? match.label : selected;
              },
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      color: "#0D0D0E",
                      fontFamily: "Inter Tight",
                      fontSize: 12,
                    },
                  },
                },
              },
            }}
            sx={{
              ...floatingFieldSx,
              ...lockedShellSx,
              width: { xs: "100%", md: 220 },
              justifySelf: "start",
            }}
          >
            {RENO_FEE_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {isReadOnly ? null : isLocked ? (
              <PrimaryButton onClick={handleUpdateRenoFee}>Update</PrimaryButton>
            ) : (
              <PrimaryButton
                onClick={handleAddRenoFee}
                disabled={calculating}
              >
                {calculating ? "Calculating..." : "Add"}
              </PrimaryButton>
            )}
          </Box>

          {(renoFeeType === "CUSTOMER_PAID" || renoFeeType === "SPLIT") && (
            <>
              <ShareNameInput
                value={customerShare.name}
                onChange={(e) =>
                  setCustomerShare((s) => ({ ...s, name: e.target.value }))
                }
                disabled={isLocked}
              />
              <AmountCell
                value={customerShare.amount}
                onChange={(e) =>
                  setCustomerShare((s) => ({
                    ...s,
                    amount: stripDigits(e.target.value),
                  }))
                }
                disabled={isLocked}
                // CUSTOMER_PAID: the customer covers the full reno fee,
                // so the amount is fixed and shown read-only. SPLIT
                // keeps both shares editable so the user can split the
                // total however they want.
                readOnly={renoFeeType === "CUSTOMER_PAID"}
              />
            </>
          )}

          {(renoFeeType === "CONTRACTOR_PAID" || renoFeeType === "SPLIT") && (
            <>
              <ShareNameInput
                value={contractorShare.name}
                onChange={(e) =>
                  setContractorShare((s) => ({ ...s, name: e.target.value }))
                }
                disabled={isLocked}
              />
              <AmountCell
                value={contractorShare.amount}
                onChange={(e) =>
                  setContractorShare((s) => ({
                    ...s,
                    amount: stripDigits(e.target.value),
                  }))
                }
                disabled={isLocked}
                // CONTRACTOR_PAID: contractor covers the full reno fee
                // so the amount is locked. SPLIT keeps it editable.
                readOnly={renoFeeType === "CONTRACTOR_PAID"}
              />
            </>
          )}
        </Box>
      </SectionCard>

      {isLocked && calcResponse && (
        <>
          {missingMilestoneIds.length > 0 && (
            <Box
              sx={{
                border: "1px solid #FCA5A5",
                backgroundColor: "#FEF2F2",
                color: "#B91C1C",
                borderRadius: "10px",
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                fontFamily: "Inter Tight",
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#B91C1C",
                  fontFamily: "Inter Tight",
                }}
              >
                These billing milestones still need a payment line:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {(billingMilestones || [])
                  .filter((m) =>
                    missingMilestoneIds.some(
                      (id) => String(id) === String(m?.id),
                    ),
                  )
                  .map((m) => (
                    <Box
                      key={m.id}
                      sx={{
                        padding: "2px 10px",
                        borderRadius: "999px",
                        backgroundColor: "#fff",
                        border: "1px solid #FCA5A5",
                        color: "#B91C1C",
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: "Inter Tight",
                      }}
                    >
                      {m.groupName || m.description || `Milestone ${m.id}`}
                    </Box>
                  ))}
              </Box>
            </Box>
          )}
          <PaymentsTable
            title="Customer Payments"
            icon={<LayersOutlinedIcon sx={{ color: "#4D6BF6", fontSize: 20 }} />}
            rows={customerSchedule.map((s, idx) => {
              const renoFee = isRenoFeeRow(s);
              return {
                id: `cust-${idx}`,
                name: s.displayName || "",
                dueDate: s.dueDate || "",
                amount: Number(s.amount) || 0,
                // Reno-fee rows have no billing milestone — drop any
                // value that may have come from the server so the
                // dropdown renders empty + disabled.
                billingMilestoneId: renoFee ? "" : resolveMilestoneId(idx),
                rowIndex: idx,
                milestoneOptions: renoFee ? [] : optionsForRow(idx),
                milestoneCellDisabled: renoFee,
              };
            })}
            totalOverride={Number(calcResponse?.paymentPlan?.totalAmount)}
            hasMilestoneColumn={milestoneOptionsRaw.length > 0}
            onMilestoneChange={handleMilestoneSelect}
            milestoneDisabled={isReadOnly}
          />
          <PaymentsTable
            title="Settlements"
            icon={<LayersOutlinedIcon sx={{ color: "#4D6BF6", fontSize: 20 }} />}
            rows={settlementsSchedule.map((s, idx) => ({
              id: `set-${idx}`,
              name: s.paymentGroup?.groupName || s.paymentGroupName || "",
              dueDate: s.dueDate || s.paymentGroup?.dueDate || "",
              amount: Number(s.amount) || 0,
            }))}
          />
        </>
      )}
    </Box>
  );
});

const HEADER_LABEL_SX = {
  fontSize: 12,
  fontWeight: 500,
  color: "#949DAA",
  fontFamily: "Inter Tight",
};

const ROW_GRID = {
  display: "grid",
  gridTemplateColumns: "48px 1.6fr 1fr 1fr",
  columnGap: 3,
  alignItems: "center",
};

const cellInputSx = (editable = false, rightAlign = false) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    fontFamily: "Inter Tight",
    fontSize: 13,
    color: "#0D0D0E",
    cursor: editable ? "text" : "not-allowed",
    boxShadow: "none",
    "&.Mui-focused": { boxShadow: "none" },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: editable ? "#4D6BF6" : "#E5E7EB",
    borderWidth: "1px",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 14px",
    color: "#0D0D0E",
    WebkitTextFillColor: "#0D0D0E",
    cursor: editable ? "text" : "not-allowed",
    textAlign: rightAlign ? "right" : "left",
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: "#9CA3AF",
    opacity: 1,
  },
});

const RowCell = ({ value, editable = false, onChange, type, rightAlign = false }) => (
  <TextField
    fullWidth
    size="small"
    value={value ?? ""}
    type={type}
    onChange={onChange}
    InputProps={{ readOnly: !editable }}
    InputLabelProps={type === "date" ? { shrink: true } : undefined}
    sx={cellInputSx(editable, rightAlign)}
  />
);

const DueDateReadonlyCell = ({ value }) => {
  const display = formatDateDisplay(value);
  return <RowCell value={display || "-"} editable={false} />;
};

// Local money formatter — `formatIntegerAmount` strips non-digits as a
// string op, so decimals like 48,666.67 round-trip to "4,866,667"
// (off by 100x). We always want a plain rounded integer with grouping
// separators for amounts displayed in the payments tables.
const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-AE", { maximumFractionDigits: 0 }).format(
    Math.round(n),
  );
};

const PaymentsTable = ({
  title,
  icon,
  rows,
  totalOverride,
  hasMilestoneColumn = false,
  onMilestoneChange,
  milestoneDisabled = false,
}) => {
  const computedTotal = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const total =
    totalOverride !== undefined && totalOverride !== null && !Number.isNaN(totalOverride)
      ? Number(totalOverride)
      : computedTotal;

  // When the caller asks for it we expand the row grid to insert a
  // "Milestone" column between Payment Name and Due Date.
  const rowGrid = hasMilestoneColumn
    ? {
        display: "grid",
        gridTemplateColumns: "48px 1.4fr 1fr 1fr 1fr",
        columnGap: 3,
        alignItems: "center",
      }
    : ROW_GRID;

  return (
    <SectionCard
      title={title}
      icon={icon}
      headerRight={
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 14,
            fontWeight: 700,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
            borderBottom: "none !important",
          }}
        >
          {renderCurrencyIconColored("black-xs-small")}
          {formatMoney(total)}
        </Box>
      }
    >
      <Box>
        <Box sx={{ ...rowGrid, mb: 1, px: 0.5 }}>
          <Typography sx={HEADER_LABEL_SX}>#</Typography>
          <Typography sx={HEADER_LABEL_SX}>Payment Name</Typography>
          {hasMilestoneColumn && (
            <Typography sx={HEADER_LABEL_SX}>Billing Milestone</Typography>
          )}
          <Typography sx={HEADER_LABEL_SX}>Due Date</Typography>
          <Typography sx={{ ...HEADER_LABEL_SX, textAlign: "right" }}>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                justifyContent: "flex-end",
              }}
            >
              Amount ({renderCurrencyIconColored("black-xs-small")})
            </Box>
          </Typography>
        </Box>

        {rows.map((r, idx) => (
          <Box key={r.id} sx={{ ...rowGrid, py: 1, px: 0.5 }}>
            <Typography
              sx={{
                fontSize: 13,
                color: "#6B7280",
                fontFamily: "Inter Tight",
              }}
            >
              {idx + 1}
            </Typography>
            <RowCell value={r.name} editable={false} />
            {hasMilestoneColumn &&
              (() => {
                const cellDisabled =
                  !!r.milestoneCellDisabled || !!milestoneDisabled;
                const hasValue =
                  r.billingMilestoneId !== "" && r.billingMilestoneId != null;
                return (
                  <TextField
                    size="small"
                    select
                    value={
                      r.billingMilestoneId == null
                        ? ""
                        : String(r.billingMilestoneId)
                    }
                    onChange={(e) =>
                      onMilestoneChange?.(r.rowIndex ?? idx, e.target.value)
                    }
                    disabled={cellDisabled}
                    SelectProps={{
                      displayEmpty: true,
                      // Reserve room on the right edge for the clear-(×)
                      // adornment so the chevron doesn't overlap.
                      IconComponent: hasValue && !cellDisabled
                        ? () => null
                        : undefined,
                      renderValue: (selected) => {
                        if (!selected) {
                          return (
                            <Box
                              component="span"
                              sx={{
                                color: "#9CA3AF",
                                fontFamily: "Inter Tight",
                                fontSize: 13,
                              }}
                            >
                              {cellDisabled ? "—" : "Select milestone"}
                            </Box>
                          );
                        }
                        const match = (r.milestoneOptions || []).find(
                          (o) => String(o.value) === String(selected),
                        );
                        return match ? match.label : selected;
                      },
                    }}
                    InputProps={{
                      endAdornment:
                        hasValue && !cellDisabled ? (
                          <InputAdornment position="end" sx={{ mr: 0.5 }}>
                            <IconButton
                              size="small"
                              aria-label="Clear milestone"
                              onMouseDown={(e) => {
                                // Prevent the Select from opening when
                                // the user clicks the clear button.
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onMilestoneChange?.(r.rowIndex ?? idx, "");
                              }}
                              sx={{ padding: "2px" }}
                            >
                              <CloseIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        fontFamily: "Inter Tight",
                        fontSize: 13,
                        color: "#0D0D0E",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E5E7EB",
                      },
                    }}
                  >
                    {(r.milestoneOptions || []).map((o) => (
                      <MenuItem
                        key={o.value}
                        value={String(o.value)}
                        disabled={!!o.disabled}
                      >
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              })()}
            <DueDateReadonlyCell value={r.dueDate} />
            <RowCell
              value={formatMoney(r.amount)}
              editable={false}
              rightAlign
            />
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
};

export default Payments;
